import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import MultiDictionary from 'typescript-collections/dist/lib/MultiDictionary';
export class Graph {
    constructor(elements) {
        this.ids = new Map();
        this.sources = new MultiDictionary();
        this.targets = new MultiDictionary();
        for (const e of elements) {
            const elem = e;
            this.ids.set(elem.elementID, e);
            if (elem.elementType === 'edge') {
                const edge = elem;
                this.sources.setValue(edge.srcElementID, edge);
                this.targets.setValue(edge.tarElementID, edge);
            }
        }
    }
    get(elementID) {
        return this.ids.get(elementID);
    }
    incoming(elementID) {
        return this.targets.getValue(elementID);
    }
    outgoing(elementID) {
        return this.sources.getValue(elementID);
    }
    ancestors(elementID, ends = new Set()) {
        // gets the target element and all its ancestors (i.e. the elements you can reach it from) in
        // depth-first search order.
        const element = this.get(elementID);
        if (element === undefined) {
            return new Set();
        }
        let node;
        if (element.elementType === 'edge') {
            node = this.get(element.srcElementID);
        }
        else {
            node = element;
        }
        const found = new Set();
        this.ancestorDFS(node, found, ends);
        return found;
    }
    ancestorDFS(node, found, ends) {
        if (found.has(node))
            return;
        found.add(node);
        if (!ends.has(node.elementID)) {
            const incoming = this.incoming(node.elementID).filter((e) => {
                return this.get(e.srcElementID) !== undefined;
            });
            for (const edge of incoming) {
                const ancestor = this.get(edge.srcElementID);
                found.add(edge);
                this.ancestorDFS(ancestor, found, ends);
            }
        }
    }
    ancestorsBFS(elementID) {
        // gets the target element and all its ancestors (i.e. the elements you can reach it from) in
        // breadth-first search order.
        const found = new Set();
        const next = [];
        // setup starting point
        const start = this.get(elementID);
        if (start) {
            found.add(start);
            next.push(start);
        }
        // breadth-first search
        for (;;) {
            const current = next.shift();
            if (!current)
                break;
            // add incoming links
            const previous = [];
            if (current.elementType === 'edge') {
                const edge = current;
                const source = this.get(edge.srcElementID);
                if (source)
                    previous.push(source);
            }
            else {
                const node = current;
                previous.push(...this.incoming(node.elementID));
            }
            // queue up incoming
            for (const prev of previous) {
                if (found.has(prev))
                    continue;
                found.add(prev);
                next.push(prev);
            }
        }
        return found;
    }
    getNodeParents(elementID) {
        const child = this.get(elementID);
        if (!child)
            throw Error(`Element ${elementID} not found.`);
        if (child.elementType === 'edge')
            throw Error(`Element ${elementID} is not a node.`);
        // data tools have no parents
        if (child.toolType === ToolType.FILE)
            return [];
        const incoming = this.incoming(elementID);
        const parents = incoming.map((edge) => {
            return this.get(edge.srcElementID);
        });
        return parents;
    }
    /**
     * Returns a set of disconnected nodes. A node is considered disconnected if it has required ins but received 0.
     * Any other nodes connected to a disconnected node is also considered disconnected. We do not check for validity
     * of the graph.
     *
     * Overview:
     * 1. Pick out edges from the graph until we have multiple subgraphs
     * 2. Iterate through each node and find any disconnected nodes, anything else connected to it is also disconnected
     */
    disconnectedNodes() {
        const connectedSets = new Set();
        const visitedNodes = new Set();
        // a map of node elementIDs to the current number of ins it has
        const nodeInsMap = new Map();
        const nodes = new Set();
        const edges = new Set();
        this.ids.forEach((e) => {
            if (e.elementType === 'edge') {
                edges.add(e);
            }
            else {
                const node = e;
                nodes.add(node);
                // each nodes currently have 0 ins, since we haven't seen any
                nodeInsMap.set(node.elementID, 0);
            }
        });
        /* form connected graphs by picking out each edge - 3 possible cases
        1. neither src/tar has been visited, so we consider them as a new subgraph
        2. both src/tar has been visited
            a) if src and tar belong to different subgraphs, then the 2 graphs must be connected and we merge
            b) if src and tar belong to same subgraph (cycle), then we do nothing (we don't care about validity here)
        3. if only 1 of src/tar has been visited, add the other to the same subgraph
        */
        edges.forEach((edge) => {
            const tarNode = this.ids.get(edge.tarElementID);
            const srcNode = this.ids.get(edge.srcElementID);
            if (!tarNode || !srcNode) {
                // This is an invalid edge
                return;
            }
            // 1. if nodes on both ends have not been seen before, make new connected set for them
            if (!visitedNodes.has(tarNode) && !visitedNodes.has(srcNode)) {
                const set = new Set();
                set.add(tarNode);
                set.add(srcNode);
                connectedSets.add(set);
                // 2. both nodes have already been visited
            }
            else if (visitedNodes.has(tarNode) && visitedNodes.has(srcNode)) {
                // if src and tar belong to 2 different sets, we need to merge the 2 sets, otherwise do nothing
                let setFound = new Set();
                for (const set of connectedSets) {
                    if (set.has(tarNode) || set.has(srcNode)) {
                        set.add(tarNode);
                        set.add(srcNode);
                        // if either tar/src has been found in another set, we need to merge with current set
                        if (setFound.size !== 0) {
                            const merged = new Set([...set, ...setFound]);
                            connectedSets.delete(set);
                            connectedSets.delete(setFound);
                            connectedSets.add(merged);
                            break;
                        }
                        else {
                            setFound = set;
                        }
                    }
                }
                // 3. only 1 of src/tar has been visited before
            }
            else {
                // similar case to having both visited, but no merging required
                for (const set of connectedSets) {
                    if (set.has(tarNode) || set.has(srcNode)) {
                        set.add(tarNode);
                        set.add(srcNode);
                        break;
                    }
                }
            }
            // add both nodes to the set of visited nodes
            visitedNodes.add(tarNode);
            visitedNodes.add(srcNode);
            // target node now has 1 more ins
            nodeInsMap.set(tarNode.elementID, nodeInsMap.get(tarNode.elementID) + 1);
        });
        // any node not visited at this point after all edges are considered must be an orphan node
        // we count orphan nodes as disconnected since there is no reasonable way to use them in a run
        let unconnectedNodes = new Set();
        nodes.forEach((node) => {
            if (!visitedNodes.has(node))
                unconnectedNodes.add(node);
        });
        // count ins for each node in a subgraph to check that it is disconnected
        // if at any point there is a node in the set that have required ins but has 0,
        // then that entire set is considered disconnected
        connectedSets.forEach((set) => {
            let disconnected = false;
            set.forEach((e) => {
                const tooltype = e.toolType;
                const ins = nodeInsMap.get(e.elementID);
                // only tool that can have 0 ins are pipe/data/Sample Customers/Monte Carlo tool
                const toolsWithNoIns = [ToolType.PIPE, ToolType.FILE, ToolType.SAMPLE_CUSTOMERS, ToolType.MONTE_CARLO];
                if (ins === 0 && !toolsWithNoIns.includes(tooltype))
                    disconnected = true;
            });
            if (disconnected)
                unconnectedNodes = new Set([...unconnectedNodes, ...set]);
        });
        return unconnectedNodes;
    }
    get elements() {
        return this.ids.values();
    }
    get groupsAndNodes() {
        return this.filter((e) => e.elementType === 'group' || e.elementType === 'node');
    }
    get groups() {
        return this.filter((e) => e.elementType === 'group');
    }
    get nodes() {
        return this.filter((e) => e.elementType === 'node');
    }
    get edges() {
        return this.filter((e) => e.elementType === 'edge');
    }
    // eslint-disable-next-line class-methods-use-this
    compareFn(a, b) {
        return a.y - b.y;
    }
    inputNodes(limit) {
        const nodes = [...this.nodes].filter((n) => n.toolType === ToolType.FILE);
        return nodes.sort(this.compareFn).slice(0, limit);
    }
    outputNodes(limit) {
        const nodes = [...this.nodes].filter((n) => n.toolType === ToolType.EXPORT);
        return nodes.sort(this.compareFn).slice(0, limit);
    }
    incentivesOutputNodes(limit) {
        const nodes = [...this.nodes].filter((n) => n.toolType === ToolType.INCENTIVES_EXPORT);
        return nodes.sort(this.compareFn).slice(0, limit);
    }
    reusableLogicNodes(limit) {
        const nodes = [...this.nodes].filter((n) => n.toolType === ToolType.REUSABLE_LOGIC);
        return nodes.sort(this.compareFn).slice(0, limit);
    }
    reusableLogicInputNodes(limit) {
        const nodes = [...this.nodes].filter((n) => n.toolType === ToolType.REUSABLE_LOGIC_INPUT);
        return nodes.sort(this.compareFn).slice(0, limit);
    }
    reusableLogicOutputNodes(limit) {
        const nodes = [...this.nodes].filter((n) => n.toolType === ToolType.REUSABLE_LOGIC_OUTPUT);
        return nodes.sort(this.compareFn).slice(0, limit);
    }
    filter(predicate) {
        const elements = Array.from(this.elements);
        const nodes = elements.filter((element) => predicate(element));
        return nodes[Symbol.iterator]();
    }
    get size() {
        return this.ids.size;
    }
    getComputeOrder() {
        // set levels
        const levels = new Map();
        for (const n of this.nodes) {
            this.setLevel(n, levels);
        }
        const labelled = [];
        for (const e of levels) {
            const item = { node: e['0'], level: e['1'] };
            labelled.push(item);
        }
        labelled.sort((a, b) => a.level - b.level);
        const ordered = labelled.map((item) => item.node);
        return ordered;
    }
    setLevel(target, levels) {
        // Compute level for a node: 1 to n, recursively computing incoming nodes' levels.
        const knownLevel = levels.get(target);
        if (knownLevel !== undefined) {
            // knownLevel may be an actual level we've previously computed or -1 indicating we're
            // in a cycle. -1 works out fine because the behavior we want is to ignore edges into
            // cycles as far as downstream levels are concerned. Since levels are computed below with
            // a max() function, default 0, -1 is always ingored.
            return knownLevel;
        }
        levels.set(target, -1); // set target as underway to detect cycles
        const incoming = this.incoming(target.elementID);
        const max = incoming.reduce((acc, value) => {
            if (this.get(value.srcElementID) !== undefined) {
                const srcNode = this.get(value.srcElementID);
                const level = this.setLevel(srcNode, levels);
                return acc > level ? acc : level;
            }
            else {
                return acc;
            }
        }, 0);
        const level = max + 1;
        levels.set(target, level);
        return level;
    }
}
//# sourceMappingURL=graph.js.map